import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';

// Async thunks
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (message, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { currentConversationId } = state.chat;
      
      // Prepare payload - only include conversation_id if it exists
      const payload = { message };
      if (currentConversationId) {
        payload.conversation_id = currentConversationId;
      }
      
      const response = await apiService.sendMessage(payload);
      const { conversation_id, reply } = response.data;
      
      // Create message objects for both user message and bot reply
      const userMessage = {
        id: Date.now(),
        text: message,
        sender: 'user',
        timestamp: new Date().toISOString(),
        conversation_id: conversation_id
      };
      
      const botMessage = {
        id: reply.metadata.id,
        text: reply.message,
        sender: 'bot',
        timestamp: reply.metadata.created_at,
        conversation_id: conversation_id,
        metadata: reply.metadata
      };
      
      return { userMessage, botMessage };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send message');
    }
  }
);

export const fetchChatHistory = createAsyncThunk(
  'chat/fetchChatHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getChatHistory();
      // Assuming the response contains an array of messages
      // If the structure is different, we'll need to adjust this
      return response.data.messages || response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch chat history');
    }
  }
);

export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getConversations();
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversations');
    }
  }
);

export const fetchConversationHistory = createAsyncThunk(
  'chat/fetchConversationHistory',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await apiService.getConversationHistory(conversationId);
      const { conversation, chats } = response.data;
      
      // Transform chats array to match our message format
      const messages = chats.map(chat => ({
        id: chat.id,
        text: chat.message,
        sender: chat.is_ai ? 'bot' : 'user',
        timestamp: chat.created_at,
        message_type: chat.message_type,
        file_url: chat.file_url,
        is_read: chat.is_read,
        created_by: chat.created_by,
        llm_metadata: chat.llm_metadata
      }));
      
      return { 
        conversationId, 
        conversation,
        data: messages 
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversation history');
    }
  }
);

export const summarizeConversation = createAsyncThunk(
  'chat/summarizeConversation',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await apiService.summarizeConversation(conversationId);
      return { conversationId, summary: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to summarize conversation');
    }
  }
);

export const pollSummaryStatus = createAsyncThunk(
  'chat/pollSummaryStatus',
  async (conversationId, { getState, rejectWithValue }) => {
    try {
      const response = await apiService.getSummaryStatus(conversationId);
      return { conversationId, status: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get summary status');
    }
  }
);

export const startSummaryPolling = createAsyncThunk(
  'chat/startSummaryPolling',
  async (conversationId, { dispatch, getState }) => {
    // Clear any existing polling interval
    const state = getState();
    if (state.chat.pollingInterval) {
      clearInterval(state.chat.pollingInterval);
    }

    // Start polling every 3 seconds
    const interval = setInterval(() => {
      dispatch(pollSummaryStatus(conversationId));
    }, 3000);

    return interval;
  }
);

export const stopSummaryPolling = createAsyncThunk(
  'chat/stopSummaryPolling',
  async (_, { getState }) => {
    const state = getState();
    if (state.chat.pollingInterval) {
      clearInterval(state.chat.pollingInterval);
    }
    return null;
  }
);

const initialState = {
  messages: [],
  conversations: [],
  currentConversationId: null,
  currentConversation: null,
  loading: false,
  conversationsLoading: false,
  summarizing: false,
  summary: null,
  summaryStatus: null,
  pollingInterval: null,
  error: null,
  isTyping: false,
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    setTyping: (state, action) => {
      state.isTyping = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentConversation: (state, action) => {
      state.currentConversationId = action.payload;
    },
    clearSummary: (state) => {
      state.summary = null;
    },
    clearSummaryStatus: (state) => {
      state.summaryStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        // Add both user message and bot reply to the messages array
        state.messages.push(action.payload.userMessage);
        state.messages.push(action.payload.botMessage);
        // Set the current conversation ID from the response
        state.currentConversationId = action.payload.userMessage.conversation_id;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch chat history
      .addCase(fetchChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.conversationsLoading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.conversationsLoading = false;
        state.error = action.payload;
      })
      // Fetch conversation history
      .addCase(fetchConversationHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversationHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload.data;
        state.currentConversationId = action.payload.conversationId;
        state.currentConversation = action.payload.conversation;
      })
      .addCase(fetchConversationHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Summarize conversation
      .addCase(summarizeConversation.pending, (state) => {
        state.summarizing = true;
        state.error = null;
      })
      .addCase(summarizeConversation.fulfilled, (state, action) => {
        state.summarizing = false;
        state.summary = action.payload.summary;
      })
      .addCase(summarizeConversation.rejected, (state, action) => {
        state.summarizing = false;
        state.error = action.payload;
      })
      // Start summary polling
      .addCase(startSummaryPolling.fulfilled, (state, action) => {
        state.pollingInterval = action.payload;
      })
      // Stop summary polling
      .addCase(stopSummaryPolling.fulfilled, (state) => {
        state.pollingInterval = null;
      })
      // Poll summary status
      .addCase(pollSummaryStatus.pending, (state) => {
        // Keep existing state, just updating status
      })
      .addCase(pollSummaryStatus.fulfilled, (state, action) => {
        state.summaryStatus = action.payload.status;
        
        // Stop polling if overall status is completed or failed
        if (action.payload.status.overall_status === 'completed' || 
            action.payload.status.overall_status === 'failed') {
          if (state.pollingInterval) {
            clearInterval(state.pollingInterval);
            state.pollingInterval = null;
          }
        }
      })
      .addCase(pollSummaryStatus.rejected, (state, action) => {
        state.error = action.payload;
        // Stop polling on error
        if (state.pollingInterval) {
          clearInterval(state.pollingInterval);
          state.pollingInterval = null;
        }
      });
  },
});

export const { addMessage, setTyping, clearMessages, clearError, setCurrentConversation, clearSummary, clearSummaryStatus } = chatSlice.actions;
export default chatSlice.reducer; 